import { useCallback, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import {
  MOBILE_BREAKPOINT_PX,
  nearestAnchor,
  nearestCorner,
  type MobileAnchor,
} from '#features/media-window/model';

/** Меньше этого сдвига — это клик по заголовку, а не перетаскивание. */
const CLICK_SLOP_PX = 6;
/** Свайп вниз на столько — сворачиваем окно в пилюлю. */
const COLLAPSE_SWIPE_PX = 80;

type CornerDragOptions = {
  /** Само окно: за ним следит палец, по нему же считается ближайший угол. */
  ref: RefObject<HTMLElement | null>;
  /** Перетаскивать можно только развёрнутое окно — полноэкранное и свёрнутое стоят на месте. */
  canDrag: boolean;
  /** Где окно стоит на телефоне сейчас — от этого зависит, что значит движение вниз. */
  anchor?: MobileAnchor;
  /** Свайп вниз на мобильном. */
  onCollapse: () => void;
  /** Отпустили на десктопе — окно прилипает к ближайшему углу. */
  onCorner: (corner: ReturnType<typeof nearestCorner>) => void;
  /** Отпустили на телефоне — окно прилипает к ближнему краю, верхнему или нижнему. */
  onAnchor?: (anchor: MobileAnchor) => void;
};

/**
 * Жест за «ручку» окна: на телефоне окно переносится между нижним и верхним краем экрана, на
 * десктопе живьём следует за курсором и на отпускании прилипает к ближайшему углу.
 *
 * Раньше на телефоне жест умел ровно одно — свернуть окно движением вниз. Уйти окну было некуда,
 * и в чате оно стояло поверх поля ввода: свернуть его значило прервать единственную связь с
 * разговором, а не свернуть — потерять возможность написать. Поэтому движение теперь читается в
 * обе стороны, а сворачивает только явный рывок вниз от нижнего края — там ниже уже ничего нет.
 */
export const useCornerDrag = ({
  ref,
  canDrag,
  anchor = 'bottom',
  onCollapse,
  onCorner,
  onAnchor,
}: CornerDragOptions) =>
  useCallback(
    (event: ReactPointerEvent) => {
      const el = ref.current;
      if (!el) {
        return;
      }
      event.preventDefault();
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
      if (!isMobile && !canDrag) {
        return;
      }

      const startX = event.clientX;
      const startY = event.clientY;
      let moved = 0;

      const onMove = (move: PointerEvent) => {
        const dx = move.clientX - startX;
        const dy = move.clientY - startY;
        moved = Math.abs(dx) + Math.abs(dy);
        el.style.transition = 'none';
        if (isMobile) {
          // Вбок двигать некуда — окно во всю ширину, — а по вертикали теперь в обе стороны.
          el.style.transform = `translateY(${dy}px)`;
        } else {
          el.style.transform = `translate(${dx}px, ${dy}px)`;
        }
      };

      const onUp = (up: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        const rect = el.getBoundingClientRect();
        el.style.transform = '';
        el.style.transition = '';

        if (isMobile) {
          const dy = up.clientY - startY;
          const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
          const next = nearestAnchor(rect.top + rect.height / 2, viewportHeight);

          // Рывок вниз от нижнего края сворачивает — под окном там уже нет экрана, и другого
          // смысла у жеста быть не может. Тот же рывок от верхнего края просто возвращает окно
          // вниз, иначе перенос наверх оказался бы дорогой в один конец.
          if (dy > COLLAPSE_SWIPE_PX && anchor === 'bottom' && next === 'bottom') {
            onCollapse();
            return;
          }
          if (moved >= CLICK_SLOP_PX && next !== anchor) {
            onAnchor?.(next);
          }
          return;
        }

        if (moved < CLICK_SLOP_PX) {
          return;
        }
        onCorner(nearestCorner(rect.left + rect.width / 2, rect.top + rect.height / 2));
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [ref, canDrag, anchor, onCollapse, onCorner, onAnchor]
  );
